import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Button, Stack, TextField, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, IconButton, Tooltip, Paper,
  Alert, Snackbar, Chip
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useDispatch, useSelector } from 'react-redux';
import {
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  Edit as EditIcon,
  AttachMoney as AttachMoneyIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Download as DownloadIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { exportToExcel, exportToPrintWindow, exportStaffDetailsToPrint } from '../utils/exportUtils';
import { useToast } from '../context/ToastContext';
import DynamicDataGrid from '../components/DynamicDataGrid';
import { deleteStaff, archiveStaff, fetchStaff, clearArchiveError } from '../slices/staffSlice';
import {
  fetchEmployeeTypes,
} from '../slices/employeeTypesSlice';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

// دالة لحساب المتبقي للموظف (إجمالي الراتب - المدفوع له)
function getRemainingSalary(staff) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // Adding 1 because salary_exceptions use 1-12 format
    
    const base = Number(staff.base_salary || staff.salary || 0);

    // Virtual transactions for current month (bonuses = credit, deductions/absence fees = debit)
    const currentMonthVirtuals = Array.isArray(staff.virtual_transactions)
        ? staff.virtual_transactions.filter(v => {
            const transactionDate = new Date(v.date);
            return transactionDate.getFullYear() === currentYear &&
                   transactionDate.getMonth() === currentMonth - 1; // -1 because getMonth() is 0-indexed
        })
        : [];

    const bonuses = currentMonthVirtuals
        .filter(v => v.direction === 'credit')
        .reduce((sum, v) => sum + Number(v.amount || 0), 0);

    const deductions = currentMonthVirtuals
        .filter(v => v.direction === 'debit')
        .reduce((sum, v) => sum + Number(v.amount || 0), 0);

    // Check for salary exceptions in current month
    const hasUnpaidLeave = Array.isArray(staff.salary_exceptions) &&
        staff.salary_exceptions.some(exception =>
            exception.year === currentYear &&
            exception.month === currentMonth &&
            exception.exception_type === 'unpaid_leave'
        );

    // If there's unpaid leave, don't include base salary
    const effectiveBaseSalary = hasUnpaidLeave ? 0 : base;

    const total = effectiveBaseSalary + bonuses - deductions;
    const paid = Number(staff.total_paid_this_month || staff.paid || 0);

    return total - paid;
}

const StaffPage = ({ isArchivedView = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  // Get data from Redux store
  const { list: staff, status, error, archiveError } = useSelector((state) => state.staff);
  const { items: employeeTypes } = useSelector((state) => state.employeeTypes);

  // Local filter state
  const [filterValues, setFilterValues] = useState({
    employee_id: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    department: ''
  });

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);

  // Error dialog state
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchStaff());
    dispatch(fetchEmployeeTypes());
  }, [dispatch]);

  // Handle fetch errors
  useEffect(() => {
    if (error && status === 'failed') {
      const errorMsg = typeof error === 'string' ? error : error.detail || error.message || 'حدث خطأ أثناء جلب بيانات الموظفين';
      showToast(errorMsg, 'error');
    }
  }, [error, status, showToast]);

  // Handle archive errors
  useEffect(() => {
    if (archiveError) {
      setErrorMessage(archiveError.message || 'حدث خطأ أثناء الأرشفة');
      setErrorDialogOpen(true);
    }
  }, [archiveError]);

  // Filter staff based on archive status and search filters
  // Handle case where staff might be null or undefined
  const filteredStaff = (staff || []).filter((row) => {
    // First filter by archive status
    const archiveMatch = row.is_archived === isArchivedView;
    
    // Then apply search filters
    const employeeIdMatch = !filterValues.employee_id || 
      (row.employee_id && row.employee_id.toLowerCase().includes(filterValues.employee_id.toLowerCase()));
    
    const firstNameMatch = !filterValues.first_name || 
      (row.first_name && row.first_name.toLowerCase().includes(filterValues.first_name.toLowerCase()));
    
    const lastNameMatch = !filterValues.last_name || 
      (row.last_name && row.last_name.toLowerCase().includes(filterValues.last_name.toLowerCase()));
    
    const phoneMatch = !filterValues.phone_number || 
      (row.phone_number && row.phone_number.includes(filterValues.phone_number));
    
    const departmentMatch = !filterValues.department || 
  row.employee_type === filterValues.department;

    return archiveMatch && employeeIdMatch && firstNameMatch && lastNameMatch && phoneMatch && departmentMatch;
  });

  const handleFilterChange = (field, value) => {
  console.log('Filter change:', field, value, typeof value); // Add this for debugging
  setFilterValues((prev) => ({ ...prev, [field]: value }));
};

  const handleAddStaff = () => {
    navigate('/staff/create');
  };

  const handleExportExcel = () => {
    const columns = [
      { header: 'رقم الهوية', key: 'employee_id' },
      { header: 'الاسم الأول', key: 'first_name' },
      { header: 'اسم العائلة', key: 'last_name' },
      { header: 'رقم الهاتف', key: 'phone_number' },
      { header: 'نوع الموظف', key: 'employee_type_name' },
      { header: 'المدفوع له', key: 'total_paid_this_month' },
      { header: 'المتبقي له', key: 'remaining_salary' },
    ];
    const data = filteredStaff.map(s => ({
      employee_id: s.employee_id || s.id_number || '',
      first_name: s.first_name,
      last_name: s.last_name,
      phone_number: s.phone_number,
      employee_type_name: getEmployeeTypeName(s.employee_type),
      total_paid_this_month: s.total_paid_this_month || s.paid || 0,
      remaining_salary: getRemainingSalary(s),
    }));
    exportToExcel(data, columns, 'staff');
  };

  const handlePrint = () => {
    const columns = [
      { header: 'رقم الهوية', key: 'employee_id' },
      { header: 'الاسم الأول', key: 'first_name' },
      { header: 'اسم العائلة', key: 'last_name' },
      { header: 'رقم الهاتف', key: 'phone_number' },
      { header: 'نوع الموظف', key: 'employee_type_name' },
      { header: 'المدفوع له', key: 'total_paid_this_month' },
      { header: 'المتبقي له', key: 'remaining_salary' },
    ];
    const data = filteredStaff.map(s => ({
      employee_id: s.employee_id || s.id_number || '',
      first_name: s.first_name,
      last_name: s.last_name,
      phone_number: s.phone_number,
      employee_type_name: getEmployeeTypeName(s.employee_type),
      total_paid_this_month: s.total_paid_this_month || s.paid || 0,
      remaining_salary: getRemainingSalary(s),
    }));
    exportToPrintWindow(data, columns, 'قائمة الموظفين');
  };

  const handleEdit = (id) => {
    navigate(`/staff/edit/${id}`);
  };

  const handleViewFinance = (id) => {
    navigate(`/staff/finance/${id}`);
  };

  const handleDeleteClick = (id) => {
    setSelectedStaffId(id);
    setOpenDialog(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteStaff(selectedStaffId));
    setOpenDialog(false);
  };

  const handleCancelDelete = () => {
    setOpenDialog(false);
  };

  const handleArchive = async (id, isCurrentlyArchived = false) => {
    try {
      // Clear any previous archive errors
      dispatch(clearArchiveError());
      
      const result = await dispatch(archiveStaff({ id, is_archived: !isCurrentlyArchived }));
      
      if (archiveStaff.fulfilled.match(result)) {
        showToast(
          isCurrentlyArchived ? 'تم استرجاع الموظف بنجاح' : 'تمت أرشفة الموظف بنجاح', 
          'success'
        );
      }
    } catch (error) {
      console.error('Archiving failed:', error);
      // Error will be handled by the useEffect that watches archiveError
    }
  };

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    dispatch(clearArchiveError());
  };

  const getEmployeeTypeName = (id) => {
    if (!id || !employeeTypes?.length) return '';
    const match = employeeTypes.find(t => t.id === id);
    return match?.display_value || '';
  };

  const columns = [
    {
      field: 'employee_id',
      headerName: 'رقم الهوية',
      flex: 1,
      renderCell: (params) => <Typography>{params.row.employee_id || params.row.id_number || '---'}</Typography>,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold">رقم الهوية</Typography>
          <TextField
            variant="standard"
            value={filterValues.employee_id || ''}
            onChange={(e) => handleFilterChange('employee_id', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true }}
            sx={{ mt: 1 }}
          />
        </Box>
      )
    },
    {
      field: 'first_name',
      headerName: 'الاسم الأول',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold">الاسم الأول</Typography>
          <TextField
            variant="standard"
            value={filterValues.first_name}
            onChange={(e) => handleFilterChange('first_name', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true }}
            sx={{ mt: 1 }}
          />
        </Box>
      )
    },
    {
      field: 'last_name',
      headerName: 'اسم العائلة',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold">اسم العائلة</Typography>
          <TextField
            variant="standard"
            value={filterValues.last_name}
            onChange={(e) => handleFilterChange('last_name', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true }}
            sx={{ mt: 1 }}
          />
        </Box>
      )
    },
    {
      field: 'phone_number',
      headerName: 'رقم الهاتف',
      flex: 1,
      renderCell: (params) => <Typography>{params.row.phone_number || '---'}</Typography>,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold">رقم الهاتف</Typography>
          <TextField
            variant="standard"
            value={filterValues.phone_number || ''}
            onChange={(e) => handleFilterChange('phone_number', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{ disableUnderline: true }}
            sx={{ mt: 1 }}
          />
        </Box>
      )
    },
   {
  field: 'employee_type',
  headerName: 'نوع الموظف',
  flex: 1,
  renderCell: (params) => {
    const employeeTypeName = getEmployeeTypeName(params.row.employee_type);
    return <Typography>{employeeTypeName}</Typography>;
  },
  renderHeader: () => (
    <Box>
      <Typography fontWeight="bold">نوع الموظف</Typography>
      <TextField
        select
        variant="standard"
        value={filterValues.department || ''}
        onChange={(e) => handleFilterChange('department', e.target.value)}
        placeholder="بحث"
        size="small"
        SelectProps={{
          native: true,
        }}
        InputProps={{ 
          disableUnderline: true,
          style: { fontSize: '0.75rem', textAlign: 'center' }
        }}
        sx={{ mt: 1 }}
      >
        <option value="">جميع الأنواع</option>
        {employeeTypes?.map((type) => (
          <option key={type.id} value={type.id}>
            {type.display_value}
          </option>
        ))}
      </TextField>
    </Box>
  )
},
    {
      field: 'total_paid_this_month',
      headerName: 'المدفوع له',
      flex: 1,
      renderCell: (params) => <Typography color="success.main">{params.row.total_paid_this_month || params.row.paid || 0} شيكل</Typography>,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold">المدفوع له</Typography>
          <TextField
            variant="standard"
            placeholder="بحث"
            size="small"
            disabled
            InputProps={{ disableUnderline: true, style: { fontSize: '0.75rem' } }}
            sx={{ mt: 1, visibility: 'hidden' }}
          />
        </Box>
      )
    },
    {
      field: 'remaining_salary',
      headerName: 'المتبقي له',
      flex: 1,
      renderCell: (params) => {
        const remaining = getRemainingSalary(params.row);
        return (
          <Typography sx={{ color: remaining >= 0 ? 'primary.main' : 'error.main', fontWeight: 700 }}>
            {remaining.toLocaleString('en-US')} شيكل
          </Typography>
        );
      },
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold">المتبقي له</Typography>
          <TextField
            variant="standard"
            placeholder="بحث"
            size="small"
            disabled
            InputProps={{ disableUnderline: true, style: { fontSize: '0.75rem' } }}
            sx={{ mt: 1, visibility: 'hidden' }}
          />
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'الإجراءات',
      flex: 1.5,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack useFlexGap direction="row" spacing={1} justifyContent="center" width="100%">
          {!isArchivedView && (
            <>
            <Tooltip title="تفاصيل الرواتب">
                <IconButton color="success" onClick={() => handleViewFinance(params.row.id)}>
                  <AttachMoneyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="تعديل الموظف">
                <IconButton color="primary" onClick={() => handleEdit(params.row.id)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="أرشفة الموظف">
                <IconButton onClick={() => handleArchive(params.row.id, false)}>
                  <ArchiveIcon fontSize="small" color="warning" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {isArchivedView && (
            <Tooltip title="استرجاع الموظف">
              <IconButton color="secondary" onClick={() => handleArchive(params.row.id, true)}>
                <ArchiveIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )
    }
  ];

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PeopleIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                {isArchivedView ? 'الموظفين المؤرشفين' : 'إدارة الموظفين'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                {isArchivedView ? 'عرض واسترجاع الموظفين المؤرشفين' : 'إضافة وإدارة وتصدير بيانات الموظفين'}
              </Typography>
            </Box>
          </Stack>

          {!isArchivedView && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddStaff}
              data-guide-id="guide-add-staff"
              sx={{
                bgcolor: '#fff',
                color: '#4338CA',
                px: 3,
                py: 1.1,
                borderRadius: 2.5,
                boxShadow: 'none',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
              }}
            >
              إضافة موظف جديد
            </Button>
          )}
        </MotionPaper>

        {/* Export Actions */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Stack useFlexGap
            direction="row"
            spacing={1.5}
            sx={{ flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center', gap: 1.5 }}
          >
            <Button
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#16A34A',
                color: '#16A34A',
                '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
              }}
            >
              تصدير Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePrint}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#4338CA',
                color: '#4338CA',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              طباعة
            </Button>
          </Stack>

          {/* Filter Summary */}
          <Chip
            label={`عرض ${filteredStaff.length} من ${staff.filter(s => s.is_archived === isArchivedView).length} موظف`}
            sx={{
              bgcolor: '#EEF2FF',
              color: '#4338CA',
              fontWeight: 700,
              fontSize: '0.8rem',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              alignSelf: { xs: 'flex-start', sm: 'center' },
            }}
          />
        </MotionPaper>

        {/* DataGrid Card */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            mb: 3,
          }}
        >
          <DynamicDataGrid
          columns={columns}
          rows={filteredStaff}
          loading={status === 'loading'}
          paginationProps={{
            labelRowsPerPage: 'عدد الصفوف في الصفحة:',
            labelDisplayedRows: ({ from, to, count }) =>
              `عرض ${from}–${to} من ${count !== -1 ? count : `أكثر من ${to}`}`,
            nextIconButtonProps: { 'aria-label': 'الصفحة التالية' },
            backIconButtonProps: { 'aria-label': 'الصفحة السابقة' },
            getItemAriaLabel: (type) => {
              if (type === 'first') return 'الصفحة الأولى';
              if (type === 'last') return 'الصفحة الأخيرة';
              if (type === 'next') return 'الصفحة التالية';
              if (type === 'previous') return 'الصفحة السابقة';
              return '';
            },
            dir: 'rtl',
            sx: {
              direction: 'rtl',
              '.MuiTablePagination-toolbar': {
                flexDirection: 'row-reverse',
                justifyContent: 'flex-start',
              },
              '.MuiTablePagination-actions': {
                marginLeft: 0,
                marginRight: '16px',
              },
            }
          }}
        />
        </MotionPaper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleCancelDelete} dir="rtl">
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد أنك تريد حذف هذا الموظف؟ لا يمكن التراجع عن هذه العملية.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>إلغاء</Button>
          <Button onClick={handleConfirmDelete} color="error">
            تأكيد الحذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Error Dialog */}
      <Dialog 
        open={errorDialogOpen} 
        onClose={handleCloseErrorDialog} 
        dir="rtl"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
          تعذر أرشفة الموظف
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
            {errorMessage}
          </Alert>
          <DialogContentText>
            يرجى حل المشكلة المذكورة أعلاه قبل محاولة أرشفة الموظف مرة أخرى.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseErrorDialog} 
            variant="contained" 
            color="primary"
            sx={{ minWidth: 100 }}
          >
            موافق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffPage;